import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import type { AssetGroup, SymbolOverride } from "../data";
import type { OverrideParty } from "./helpers";

import { Button as ButtonV2 } from "../../../Button";
import { SegmentControl } from "../../../SegmentControl";
import { SelectV3 } from "../../../SelectV3";
import { Typo } from "../../../Typo";
import { WizardFooter } from "../CreateProgramWizard/WizardFooter";
import {
  buildAssetClassOfSymbol,
  toAssetClasses,
  toCatalogueSymbols,
} from "./catalogue";
import { findShadowedAssetClass } from "./helpers";
import { ScopedSplit } from "./ScopedSplit";
import { ShadowNote } from "./SplitLegend";

type ScopeType = SymbolOverride["scopeType"];

/**
 * Add / edit a single override rule.
 *
 * `existingRules` and `editIndex` are what enforce one rule per scope: already
 * covered scopes are omitted from the picker rather than shown disabled, since
 * SelectV3 has no disabled-row support. The rule being edited is excluded from
 * that exclusion so its own scope stays selectable.
 */
export const RuleEditor = ({
  parties,
  initial,
  existingRules,
  editIndex,
  onCancel,
  onSave,
  onDirtyChange,
  assets,
  isMobile,
  footerContainer,
}: {
  parties: OverrideParty[];
  initial: SymbolOverride | null;
  existingRules: SymbolOverride[];
  editIndex: number | null;
  onCancel: () => void;
  onSave: (rule: SymbolOverride) => void;
  onDirtyChange?: (dirty: boolean) => void;
  assets?: AssetGroup[] | null;
  isMobile?: boolean;
  footerContainer?: Element | null;
}) => {
  const { t } = useTranslation();
  const SCOPE_OPTIONS: { label: string; value: ScopeType }[] = [
    { label: t("subIBReport_overrideScopeSymbol", "Symbol"), value: "symbol" },
    {
      label: t("subIBReport_overrideScopeAssetClass", "Asset class"),
      value: "assetClass",
    },
  ];
  const catalogue = toCatalogueSymbols(assets);
  const assetClasses = toAssetClasses(assets);
  const lookupAssetClass = buildAssetClassOfSymbol(assets);

  const [scopeType, setScopeType] = useState<ScopeType>(
    initial?.scopeType ?? "symbol",
  );
  const [scopeValue, setScopeValue] = useState<string | null>(
    initial?.scopeValue ?? null,
  );
  const [pcts, setPcts] = useState<number[]>(() =>
    initial
      ? initial.partnerSplits.map((p) => p.splitPct)
      : parties.map(() => 0),
  );
  const [entered, setEntered] = useState<boolean[]>(() =>
    initial ? initial.partnerSplits.map(() => true) : parties.map(() => false),
  );

  // Captured once so a later Save doesn't shift the baseline out from under an
  // in-progress edit -- comparing against the initial values (not just "has any
  // setter fired") also means reverting every field back to them clears dirty.
  const [baseline] = useState({
    scopeType: initial?.scopeType ?? "symbol",
    scopeValue: initial?.scopeValue ?? null,
    pcts: initial
      ? initial.partnerSplits.map((p) => p.splitPct)
      : parties.map(() => 0),
  });
  const dirty =
    scopeType !== baseline.scopeType ||
    scopeValue !== baseline.scopeValue ||
    pcts.some((pct, index) => pct !== baseline.pcts[index]);
  useEffect(() => {
    onDirtyChange?.(dirty);
  }, [dirty, onDirtyChange]);

  const isSymbol = scopeType === "symbol";
  const covered = existingRules
    .filter(
      (rule, index) => index !== editIndex && rule.scopeType === scopeType,
    )
    .map((rule) => rule.scopeValue);

  const options = (
    isSymbol
      ? catalogue.map((entry) => ({
          label: entry.symbol,
          value: entry.symbol,
          description: entry.assetClass,
        }))
      : assetClasses.map((assetClass) => ({
          label: assetClass,
          value: assetClass,
        }))
  ).filter((option) => !covered.includes(option.value));

  const sum = pcts.reduce((total, n) => total + (n || 0), 0);
  const masterShare = 100 - sum;
  const canSave = !!scopeValue && entered.some(Boolean) && masterShare >= 0;

  // Switching scope type invalidates the selection — a symbol is never a valid
  // asset class and vice versa.
  const changeScopeType = (next: ScopeType) => {
    if (next === scopeType) return;
    setScopeType(next);
    setScopeValue(null);
  };

  const handleSplitChange = (index: number, pct: number, hasValue: boolean) => {
    setPcts((prev) => prev.map((n, i) => (i === index ? pct : n)));
    setEntered((prev) => prev.map((b, i) => (i === index ? hasValue : b)));
  };

  const shadowedAssetClass = findShadowedAssetClass(
    isSymbol ? scopeValue : null,
    existingRules,
    lookupAssetClass,
    editIndex,
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-6">
      <div className="flex flex-col gap-6">
        <Typo
          element="h2"
          type={isMobile ? "headingLG" : "headingXL"}
          textColor="text-text-primary"
        >
          {editIndex === null
            ? t("subIBReport_addOverrideTitle", "Add override")
            : t("subIBReport_editOverrideTitle", "Edit override")}
        </Typo>

        <div className="flex flex-col gap-6">
          <SegmentControl
            options={SCOPE_OPTIONS}
            value={scopeType}
            onChange={changeScopeType}
            fullWidth
          />

          <div className="flex flex-col gap-2">
            <SelectV3
              label={
                isSymbol
                  ? t("subIBReport_overrideScopeSymbol", "Symbol")
                  : t("subIBReport_overrideScopeAssetClass", "Asset class")
              }
              // labelSM rather than SelectV3's default labelMD (design QA).
              // Its `smLabelType` already defaults to labelSM, so setting this
              // makes the label labelSM at every width.
              labelType="labelSM"
              placeholder={
                isSymbol
                  ? t("subIBReport_selectSymbolPlaceholder", "Select a symbol")
                  : t(
                      "subIBReport_selectAssetClassPlaceholder",
                      "Select an asset class",
                    )
              }
              value={
                scopeValue ? { label: scopeValue, value: scopeValue } : null
              }
              options={options}
              onChange={(option) => setScopeValue(option.value)}
              enableSearch={isSymbol}
              modal
            />
            {shadowedAssetClass && scopeValue && (
              <ShadowNote
                assetClass={shadowedAssetClass}
                symbol={scopeValue}
                lead
              />
            )}
          </div>

          <ScopedSplit
            parties={parties}
            pcts={pcts}
            entered={entered}
            onChange={handleSplitChange}
            isMobile={isMobile}
          />
        </div>
      </div>

      {/* TODO: remove IIFE per "No IIFEs in JSX" (AGENTS.md) — pick basic/medium/complex tier */}
      {(() => {
        const footer = (
          <WizardFooter isMobile={isMobile}>
            <ButtonV2 variant="tonal" size="md" onClick={onCancel}>
              {t("subIBReport_cancel", "Cancel")}
            </ButtonV2>
            <ButtonV2
              variant="brand"
              size="md"
              disabled={!canSave}
              onClick={() =>
                onSave({
                  scopeType,
                  // `canSave` gates the button, so scopeValue is set by here.
                  scopeValue: scopeValue as string,
                  masterSplitPct: masterShare,
                  partnerSplits: parties.map((party, index) => ({
                    subIbId: party.subIbId,
                    splitPct: pcts[index],
                  })),
                })
              }
            >
              {t("subIBReport_saveRule", "Save rule")}
            </ButtonV2>
          </WizardFooter>
        );
        return footerContainer ? createPortal(footer, footerContainer) : footer;
      })()}
    </div>
  );
};
